import React, { Children } from 'react';
import { PageHero } from '../components/PageHero';
import { SectionStage } from '../components/SectionStage';
import { Link } from 'react-router-dom';
/**
 * Privacy Policy — plainspoken, scoped to the pre-GA early-access period.
 * Generic enough to be defensible, specific enough to be useful, written in
 * the Periscan voice (direct, no buzzword soup). Not legal advice — Periscan
 * will replace this with counsel-reviewed copy before GA.
 */
const LAST_UPDATED = '2026-01-15';
const sections: {
  title: string;
  body: React.ReactNode;
}[] = [{
  title: '1. Who this applies to',
  body: <>
        <p data-id="element-1785">
          This Privacy Policy describes how <strong data-id="element-1786">Periscan, Inc.</strong>{' '}
          ("Periscan", "we", "us") collects, uses, and protects information when
          you interact with our website at <em data-id="element-1787">periscan.com</em>, our
          early-access program, our marketing communications, and any related
          services (collectively, the "Services").
        </p>
        <p data-id="element-1788">
          Periscan is in early access. The Services are evolving, and so is this
          policy. We will note material changes at the top of this page and,
          where appropriate, by email.
        </p>
      </>
}, {
  title: '2. Information we collect',
  body: <>
        <p data-id="element-1789">
          We collect the minimum information needed to talk to you, qualify
          early-access fit, and operate the website. Specifically:
        </p>
        <ul data-id="element-1790">
          <li data-id="element-1791">
            <strong data-id="element-1792">You give us.</strong> Work email, name, company, role,
            company website, intended use case, current cloud and identity
            stack, security tools used, whether you have AI applications to
            validate, whether you are an MSSP or vCISO, and what made you look
            for this. You provide this through forms on the site.
          </li>
          <li data-id="element-1793">
            <strong data-id="element-1794">Automatically.</strong> Standard server logs (IP address,
            user agent, referrer, timestamp), aggregate analytics (page views,
            session duration, route changes), and cookie or local-storage data
            required for basic site function.
          </li>
          <li data-id="element-1795">
            <strong data-id="element-1796">From third parties.</strong> Publicly available enrichment
            data about the company associated with your work email (for example,
            headcount, industry, region) used solely to triage early-access
            requests.
          </li>
        </ul>
        <p data-id="element-1797">
          We do not buy contact lists. We do not enrich personal contact
          information. We do not run cross-site advertising trackers.
        </p>
      </>
}, {
  title: '3. How we use information',
  body: <>
        <ul data-id="element-1798">
          <li data-id="element-1799">To respond to early-access requests and qualify program fit.</li>
          <li data-id="element-1800">To operate, secure, and improve the website and the Services.</li>
          <li data-id="element-1801">
            To send transactional messages (early-access status, account
            communications, security notices) and, only if you opt in,
            occasional product update emails.
          </li>
          <li data-id="element-1802">
            To prevent abuse, fraud, and unauthorized access — including
            standard security logging and rate limiting.
          </li>
          <li data-id="element-1803">To comply with legal obligations and enforce our Terms.</li>
        </ul>
        <p data-id="element-1804">
          We do not sell personal information. We do not use personal
          information to train third-party AI models.
        </p>
      </>
}, {
  title: '4. Cookies and similar technologies',
  body: <>
        <p data-id="element-1805">
          The Periscan website uses a small number of strictly functional
          cookies and local-storage entries — for example, to remember your
          theme preference and to keep your session safe. We use privacy-aware
          analytics (no third-party advertising trackers, no cross-site
          profiling) to understand how the site is used in aggregate.
        </p>
        <p data-id="element-1806">
          You can clear cookies and local storage at any time through your
          browser settings. The site will continue to function without them.
        </p>
      </>
}, {
  title: '5. How we share information',
  body: <>
        <p data-id="element-1807">We share information only as needed to operate the Services:</p>
        <ul data-id="element-1808">
          <li data-id="element-1809">
            <strong data-id="element-1810">Service providers.</strong> Vetted vendors that help us run
            the Services (hosting, email delivery, analytics, customer
            communications, error monitoring). They process data on our behalf
            under contractual confidentiality and security obligations.
          </li>
          <li data-id="element-1811">
            <strong data-id="element-1812">Legal and safety.</strong> When required by law, valid legal
            process, or to protect the rights, property, or safety of Periscan,
            our users, or others.
          </li>
          <li data-id="element-1813">
            <strong data-id="element-1814">Business transfers.</strong> If we are involved in a merger,
            acquisition, or asset sale, your information may transfer to the
            successor entity under terms at least as protective as these.
          </li>
        </ul>
        <p data-id="element-1815">
          We do not sell or rent personal information to third parties for their
          own marketing.
        </p>
      </>
}, {
  title: '6. Validation data',
  body: <>
        <p data-id="element-1816">
          When Periscan begins active validation engagements with a customer, a
          separate Master Subscription Agreement and Data Processing Addendum
          will govern the data Periscan accesses inside that customer's
          environment. Those agreements include scope, authorization controls,
          policy controls, rate limits, audit logging, no-destructive-action
          guarantees, and no real data exfiltration.
        </p>
        <p data-id="element-1817">
          Until those agreements are in place, Periscan does not access customer
          environments. Early-access conversations are scoped to questionnaires,
          discovery calls, and product feedback — not live validation.
        </p>
      </>
}, {
  title: '7. Data retention',
  body: <p data-id="element-1818">
        We retain personal information only as long as we need it for the
        purposes above, or as required by law. Early-access form submissions are
        retained for the duration of the early-access program plus a reasonable
        archival period (typically up to 24 months) for fairness and program
        continuity. You may request deletion at any time — see Section 8.
      </p>
}, {
  title: '8. Your rights',
  body: <>
        <p data-id="element-1819">
          Depending on where you live, you may have rights to access, correct,
          delete, port, or restrict our processing of your personal information,
          and to object to certain processing. To exercise any right, email{' '}
          <a href="mailto:privacy@periscan.com" data-id="element-1820">privacy@periscan.com</a>. We
          will verify your request and respond within the timeframe required by
          applicable law.
        </p>
        <p data-id="element-1821">
          You can also unsubscribe from product update emails at any time using
          the link in any such email. Transactional messages about your account
          or early-access status will continue as needed.
        </p>
      </>
}, {
  title: '9. Security',
  body: <p data-id="element-1822">
        Periscan implements administrative, technical, and physical safeguards
        appropriate to the data we process — including encryption in transit,
        access controls on internal systems, vendor due diligence, and ongoing
        security monitoring. No system is perfectly secure, but validating that
        defenses actually work is literally our product. We treat it that way
        internally too.
      </p>
}, {
  title: '10. International transfers',
  body: <p data-id="element-1823">
        Periscan is based in the United States. If you access the Services from
        elsewhere, your information may be processed in the U.S. or in other
        countries where our service providers operate, subject to appropriate
        safeguards (such as Standard Contractual Clauses) where required by law.
      </p>
}, {
  title: '11. Children',
  body: <p data-id="element-1824">
        The Services are not directed to children under 16, and we do not
        knowingly collect personal information from children. If you believe a
        child has provided us personal information, contact us at{' '}
        <a href="mailto:privacy@periscan.com" data-id="element-1825">privacy@periscan.com</a> and we
        will delete it.
      </p>
}, {
  title: '12. Changes to this policy',
  body: <p data-id="element-1826">
        We may update this Privacy Policy from time to time. When we do, we will
        revise the "Last updated" date above and, for material changes, provide
        additional notice (for example, by email or a banner on the site).
      </p>
}, {
  title: '13. Contact',
  body: <>
        <p data-id="element-1827">Questions, requests, or complaints?</p>
        <ul data-id="element-1828">
          <li data-id="element-1829">
            Email:{' '}
            <a href="mailto:privacy@periscan.com" data-id="element-1830">privacy@periscan.com</a>
          </li>
          <li data-id="element-1831">
            Mail: Periscan, Inc. — address available on request via the email
            above
          </li>
        </ul>
      </>
}];
export function Privacy() {
  return <>
      <PageHero eyebrow="Privacy" title="Privacy Policy." titleMuted="Plainspoken." description="How Periscan collects, uses, and protects information during the early-access period. Written to be readable — not to hide things." bg="none" data-id="element-1832" />

      <SectionStage fx="none" data-id="element-1833">
        <section className="relative w-full py-32 border-t border-white/5" data-id="element-1834">
          <div className="max-w-3xl mx-auto px-6 lg:px-8" data-id="element-1835">
            <div className="flex items-baseline justify-between gap-4 mb-12 pb-6 border-b border-white/5" data-id="element-1836">
              <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40" data-id="element-1837">
                Last updated · {LAST_UPDATED}
              </p>
              <Link to="/terms" className="text-xs text-white/50 hover:text-[#1a8fd4] transition-colors underline underline-offset-4 decoration-white/20" data-id="element-1838">
                Read the Terms →
              </Link>
            </div>

            <div className="prose prose-invert max-w-none space-y-12 text-[15px] leading-relaxed text-white/75 [&_h2]:text-white [&_h2]:text-xl [&_h2]:font-semibold [&_h2]:tracking-tight [&_h2]:mb-4 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2 [&_ul]:mb-4 [&_a]:text-[#1a8fd4] [&_a:hover]:underline [&_strong]:text-white" data-id="element-1839">
              {sections.map(s => <article key={s.title} data-id="element-1840">
                  <h2 data-id="element-1841">{s.title}</h2>
                  {s.body}
                </article>)}

              <p className="pt-12 mt-12 border-t border-white/5 text-xs text-white/40 italic" data-id="element-1842">
                This Privacy Policy is provided for informational purposes
                during the early-access period and does not constitute legal
                advice. Use of the Services is governed by the{' '}
                <Link to="/terms" className="text-[#1a8fd4] hover:underline" data-id="element-1843">
                  Terms of Service
                </Link>{' '}
                and any executed customer agreement.
              </p>
            </div>
          </div>
        </section>
      </SectionStage>
    </>;
}